/**
 * @file components/log/MealHistory.jsx
 */

import {
  ChevronRight,
  Crown,
  Loader2,
  Pencil,
  Plus,
  Tag,
  Trash2,
  Utensils,
} from "lucide-react";
import { useState } from "react";
import { useMealMutations } from "../../hooks/useMealMutations";
import { useSubscription } from "../../hooks/useSubscription";
import { useMeals } from "../../hooks/useMeals";
import { useProfile } from "../../hooks/useProfile";
import { useDayTags } from "../../hooks/useDayTags";
import { calculateUserTargets } from "../../utils/calculateUserTargets";
import {
  formatDate,
  groupByDate,
  sortGroupedByDate,
} from "../../utils/helpers";
import EntryMealModal from "./EntryMealModal";
import { UpgradeModal } from "../shared/UpgradeModal";
import { useAuth } from "../../utils/AuthContext";

export default function MealHistory({ onShowAuth }) {
  const { uid } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeMeal, setActiveMeal] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [date, setDate] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [visibleCount, setVisibleCount] = useState(7);

  // 1. Fetch data directly via hooks
  const { data: meals = [], isLoading: mealsLoading } = useMeals(uid);
  const { data: profileData } = useProfile(uid);
  const { isPremium } = useSubscription(uid);
  const { tags: dayTags, setTag, removeTag } = useDayTags(uid);

  // 2. Use centralized mutations
  const { addMealMutation, deleteMealMutation, updateMealMutation } =
    useMealMutations(uid);

  // UI loading state combined
  if (mealsLoading) return <Skeleton />;

  // 3. Handlers using mutations
  async function onAddMealForDate(newMeal, description) {
    addMealMutation.mutate(
      {
        description: description || "Custom Meal",
        calories: newMeal.totalCal,
        protein: newMeal.totalPro,
        items: newMeal.items || [],
        source: newMeal.source || "Custom Entry",
        date,
      },
      {
        onSuccess: () => setIsModalOpen(false),
      },
    );
  }

  async function handleDelete(mealId) {
    deleteMealMutation.mutate(mealId);
  }

  async function handleEdit(mealId, newMeal, description) {
    updateMealMutation.mutate(
      {
        mealId,
        data: {
          description: description || "Custom Meal",
          calories: newMeal.totalCal,
          protein: newMeal.totalPro,
          items: newMeal.items || [],
        },
      },
      {
        onSuccess: () => setIsEdit(false),
      },
    );
  }

  const handleDateClick = (dateStr) => {
    setIsModalOpen(true);
    setActiveMeal(null); // Clear the active meal
    setIsEdit(false); // Ensure we're in "add" mode
    const createdAt = new Date(dateStr);
    createdAt.setHours(23, 59, 59, 999);
    setDate(createdAt.toISOString());
  };

  if (!meals.length) return <EmptyState />;

  const sortedGrouped = sortGroupedByDate(groupByDate(meals));
  const allEntries = Object.entries(sortedGrouped);
  const visibleEntries = isPremium ? allEntries.slice(0, visibleCount) : allEntries.slice(0, 7);
  const hasMorePremium = isPremium && visibleCount < allEntries.length;
  const hasHiddenHistory = !isPremium && allEntries.length > 7;

  return (
    <div className="mt-10 space-y-1">
      <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-6 px-1">
        Recent History
      </h4>

      {visibleEntries.map(([date, dayMeals], index) => (
        <DateGroup
          key={date}
          date={date}
          dayMeals={dayMeals}
          deletingId={deleteMealMutation.variables}
          isDeleting={deleteMealMutation.isPending}
          handleDelete={handleDelete}
          onAddMealForDate={handleDateClick}
          profileData={profileData}
          isOpenDefault={index === 0}
          handleMealclick={(meal) => {
            setIsEdit(true);
            setActiveMeal(meal);
          }}
          dayTag={dayTags[date] || null}
          onSetTag={(tag) => setTag(date, tag)}
          onRemoveTag={() => removeTag(date)}
        />
      ))}

      {/* Free tier history limit CTA */}
      {hasHiddenHistory && (
        <button
          onClick={() => setShowUpgrade(true)}
          style={{ touchAction: "manipulation" }}
          className="w-full mt-4 p-4 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center gap-3 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all group"
        >
          <Crown size={16} className="text-brand-amber group-hover:text-brand-green transition-colors" />
          <span className="text-xs text-gray-500 group-hover:text-gray-300 font-medium transition-colors">
            Unlock full history ({allEntries.length - 7} more days) — Go Premium
          </span>
        </button>
      )}

      {/* Premium "View More" pagination */}
      {hasMorePremium && (
        <button
          onClick={() => setVisibleCount((prev) => prev + 7)}
          style={{ touchAction: "manipulation" }}
          className="w-full mt-4 p-4 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-center gap-3 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all group"
        >
          <ChevronRight size={16} className="text-gray-500 group-hover:text-brand-green transition-colors" />
          <span className="text-xs text-gray-500 group-hover:text-gray-300 font-medium transition-colors">
            View more ({allEntries.length - visibleCount} more days)
          </span>
        </button>
      )}

      {(isModalOpen || isEdit) && (
        <EntryMealModal
          onClose={() => {
            setIsModalOpen(false);
            setIsEdit(false);
            setActiveMeal(null);
          }}
          onSave={onAddMealForDate}
          onEdit={handleEdit}
          isDietPlan={false}
          meal={activeMeal}
          isEdit={isEdit}
        />
      )}

      {showUpgrade && (
        <UpgradeModal reason="history" onClose={() => setShowUpgrade(false)} onShowAuth={onShowAuth} />
      )}
    </div>
  );
}

// Sub-components (Logic remains same, but using cleaner props)
function DateGroup({
  date,
  dayMeals,
  deletingId,
  isDeleting,
  handleDelete,
  onAddMealForDate,
  profileData,
  isOpenDefault,
  handleMealclick,
  dayTag,
  onSetTag,
  onRemoveTag,
}) {
  const [isOpen, setIsOpen] = useState(isOpenDefault);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const dynamicTargets = calculateUserTargets(profileData);
  const dayTotal = dayMeals.reduce((s, m) => s + (m.calories || 0), 0);
  const dayProtein = dayMeals.reduce((s, m) => s + (m.protein || 0), 0);

  const isOver = dayTotal > dynamicTargets.calories;
  const barColor = isOver ? "bg-red-500" : "bg-emerald-500";
  const pct = Math.min((dayTotal / dynamicTargets.calories) * 100, 100);

  const PRESET_TAGS = ["Free", "Strict", "Deficit", "Maintenance", "Cheat Day", "High Protein", "Rest Day"];

  const TAG_COLORS = {
    free: { bg: "bg-sky-500/15", border: "border-sky-400/30", text: "text-sky-400" },
    strict: { bg: "bg-red-500/15", border: "border-red-400/30", text: "text-red-400" },
    deficit: { bg: "bg-orange-500/15", border: "border-orange-400/30", text: "text-orange-400" },
    maintenance: { bg: "bg-emerald-500/15", border: "border-emerald-400/30", text: "text-emerald-400" },
    "cheat day": { bg: "bg-pink-500/15", border: "border-pink-400/30", text: "text-pink-400" },
    "high protein": { bg: "bg-amber-500/15", border: "border-amber-400/30", text: "text-amber-400" },
    "rest day": { bg: "bg-indigo-500/15", border: "border-indigo-400/30", text: "text-indigo-400" },
  };

  function getTagColor(tag) {
    const key = tag.toLowerCase();
    return TAG_COLORS[key] || { bg: "bg-purple-500/15", border: "border-purple-400/30", text: "text-purple-400" };
  }

  function handleToggleTag(tag) {
    if (dayTag && dayTag.toLowerCase() === tag.toLowerCase()) {
      onRemoveTag();
    } else {
      onSetTag(tag);
    }
  }

  return (
    <div
      className={`mb-4 transition-all duration-300 relative ${isOpen ? "bg-white/[0.02] p-3 rounded-2xl border border-white/5" : ""}`}
    >
      {/* Day tag badge - positioned top-right, rotated */}
      {dayTag && (() => {
        const colors = getTagColor(dayTag);
        return (
          <span className={`absolute -top-1.5 right-2 px-2 py-0.5 rounded-md ${colors.bg} border ${colors.border} ${colors.text} text-[8px] font-bold uppercase tracking-wider rotate-[-2deg] z-10 shadow-sm`}>
            {dayTag}
          </span>
        );
      })()}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex flex-col gap-2 group p-2"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChevronRight
              size={16}
              className={`text-gray-600 transition-transform ${isOpen ? "rotate-90 text-emerald-500" : ""}`}
            />
            <p className="text-sm font-bold text-gray-200 group-hover:text-white transition-colors">
              {date}
            </p>
            {!dayTag && !isOpen && (
              <span
                onClick={(e) => { e.stopPropagation(); setIsOpen(true); setTimeout(() => setShowTagPicker(true), 100); }}
                className="p-1 rounded-md text-gray-600 hover:text-gray-400 transition-colors"
                title="Add day tag"
              >
                <Tag size={12} />
              </span>
            )}
          </div>
          <div className="text-right flex items-center gap-4">
            <div>
              <span className="text-sm font-bold text-emerald-400">
                {dayProtein.toFixed(0)}
              </span>
              <span className="text-[10px] text-gray-600 font-medium ml-1">
                g pro
              </span>
            </div>
            <div>
              <span
                className={`text-sm font-bold ${isOver ? "text-red-400" : "text-emerald-400"}`}
              >
                {dayTotal}
              </span>
              <span className="text-[10px] text-gray-600 font-medium ml-1">
                / {dynamicTargets.calories} kcal
              </span>
            </div>
          </div>
        </div>
        <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-1000 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </button>

      {isOpen && (
        <div className="mt-4 space-y-2 animate-in slide-in-from-top-2 duration-300">
          {/* Day tag controls — toggle preset tags on/off */}
          <div className="mb-2">
            {!dayTag && !showTagPicker ? (
              <button
                onClick={(e) => { e.stopPropagation(); setShowTagPicker(true); }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-dashed border-white/10 text-gray-600 hover:text-gray-400 hover:border-white/20 transition-colors"
              >
                <Tag size={10} />
                <span className="text-[10px] font-medium">Add day tag</span>
              </button>
            ) : (
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 flex flex-wrap gap-1.5 animate-in fade-in duration-150">
                {PRESET_TAGS.map((preset) => {
                  const colors = getTagColor(preset);
                  const isActive = dayTag?.toLowerCase() === preset.toLowerCase();
                  return (
                    <button
                      key={preset}
                      onClick={() => handleToggleTag(preset)}
                      className={`px-2.5 py-1 rounded-full ${colors.bg} border ${colors.border} text-[9px] font-bold ${colors.text} transition-all uppercase tracking-wider ${isActive ? "opacity-100 ring-1 ring-current" : "opacity-60 hover:opacity-100"}`}
                    >
                      {preset}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {dayMeals.map((meal) => (
            <MealEntry
              key={meal.id}
              meal={meal}
              isDeleting={isDeleting && deletingId === meal.id}
              onDelete={() => handleDelete(meal.id)}
              onEdit={() => handleMealclick(meal)}
            />
          ))}
          <button
            onClick={() => onAddMealForDate(date)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/10 text-gray-500 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all text-xs font-bold"
          >
            <Plus size={14} /> Add to {date}
          </button>
        </div>
      )}
    </div>
  );
}

function MealEntry({ meal, isDeleting, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden hover:border-white/10 transition-all shadow-sm">
      <div className="flex items-center justify-between p-4 gap-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left min-w-0"
        >
          <p className="text-sm text-gray-200 font-semibold truncate leading-none mb-1.5">
            {meal.description}
          </p>
          <p className="text-[10px] text-gray-600 uppercase font-bold tracking-wider">
            {formatDate(meal.createdAt)}
          </p>
        </button>

        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-sm font-black text-emerald-500 leading-none">
              {(meal.protein || 0).toFixed(0)}
            </p>
            <p className="text-[8px] text-gray-600 uppercase font-bold">Pro</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-black text-amber-500 leading-none">
              {meal.calories}
            </p>
            <p className="text-[8px] text-gray-600 uppercase font-bold">Kcal</p>
          </div>
          <div className="flex gap-1">
            <button
              onClick={onEdit}
              disabled={isDeleting}
              className="p-2 text-gray-600 hover:text-blue-400 rounded-lg transition-all"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="p-2 text-gray-600 hover:text-red-400 rounded-lg transition-all"
            >
              {isDeleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          </div>
        </div>
      </div>
      {expanded && meal.items?.length > 0 && (
        <div className="px-4 pb-4 pt-1 bg-white/[0.01] border-t border-white/5 space-y-2 animate-in fade-in duration-200">
          {meal.items.map((item, i) => (
            <div
              key={i}
              className="flex justify-between items-center text-[11px] font-medium text-gray-500 border-b border-white/[0.03] pb-1 last:border-0"
            >
              <span className="capitalize">{item.name}</span>
              <span className="text-amber-500/80 font-bold">
                {item.cal} kcal
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Utility Components
function EmptyState() {
  return (
    <div className="text-center py-20 px-6 bg-white/5 rounded-3xl border border-dashed border-white/10 mt-10">
      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
        <Utensils className="text-gray-600" size={24} />
      </div>
      <h3 className="text-white font-bold text-lg">No meals logged</h3>
      <p className="text-gray-500 text-sm mt-1">
        Start tracking to see your history here.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4 mt-10 animate-pulse px-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-4 w-32 bg-white/5 rounded mx-1" />
          <div className="h-20 bg-white/5 rounded-2xl border border-white/5" />
        </div>
      ))}
    </div>
  );
}
